import { Box, FormControl, FormHelperText, InputProps, Stack } from "@mui/material";
import { FormikErrors } from "formik";
import _ from "lodash";

import { MemoHelper, memoWithReversedArgs } from "@/common/helpers/memo";
import { enumService } from "@/common/services/enum";
import { ValidationHelper } from "@/common/validation";
import {
  BillingPeriod,
  GeneralCurrencyInputDto,
  SubscriptionPlanPriceInputDto,
  VehicleTaxCalcRequestDto,
} from "@/core/api/generated/v1";

import ApiEnumAutocomplete from "../../Enum/ApiEnumAutocomplete";
import PriceSummaryInput from "../../PriceSummary/PriceSummaryInput";

type ValueType = SubscriptionPlanPriceInputDto & {
  total?: number;
};

interface Props {
  currency: Nil<GeneralCurrencyInputDto>;
  value: Nil<ValueType>;
  errors?: FormikErrors<ValueType> | string;
  inputsProps?: {
    /** Applied to all inputs */
    all?: {
      size?: InputProps["size"];
    };
  };
  excludeBillingPeriods?: BillingPeriod[];
  /** If passed, tax will be recalculated for specified vehicle params. */
  vehicleTaxCalcRequestParams?: VehicleTaxCalcRequestDto;
  onChange: (params: {
    currency: GeneralCurrencyInputDto | undefined;
    newValue: ValueType | undefined;
  }) => void;
}

export default memoWithReversedArgs(
  MemoHelper.arePropsEqual.factory<Props>({
    isExcludeFunctionProps: true,
    excludeProps: (t) => [t.inputsProps.$path],
  }),
  function SubscriptionPlanPriceInput({
    currency,
    value,
    inputsProps,
    errors,
    excludeBillingPeriods,
    vehicleTaxCalcRequestParams,
    onChange,
  }: Props) {
    const generalError = _.isString(errors) ? errors?.toString() : undefined;
    const typedErrors = _.isObject(errors) ? errors : undefined;

    return (
      <Stack spacing={0}>
        {/* Currency */}
        {/* <FormControl fullWidth margin='dense'>
          <CurrencyAutocomplete
            required
            entity={values.currency}
            textFieldProps={{
              label: "Currency",
              margin: "dense",
              error: Boolean(errors.currency),
              helperText: ValidationHelper.getFormikErrorsAsString(errors.currency),
            }}
            onChange={(newValue) => setFieldValue("currency", newValue)}
          />
        </FormControl> */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              sm: "repeat(1, 1fr)",
              md: "repeat(2, 1fr)",
              xl: "repeat(3, 1fr)",
            },
          }}
        >
          <FormControl margin='dense' fullWidth required error={Boolean(typedErrors?.period)}>
            <ApiEnumAutocomplete
              type='BillingPeriod'
              value={value?.period}
              excludeEnumValues={excludeBillingPeriods}
              onChange={(newValue) => {
                onChange({
                  currency: currency || undefined,
                  newValue: {
                    ...value,
                    period: newValue || undefined,
                  },
                });
              }}
              label='Billing period'
              required
              textFieldProps={{
                error: Boolean(typedErrors?.period),
                helperText: ValidationHelper.getErrorsAsString(typedErrors?.period),
                ...inputsProps?.all,
              }}
            />
          </FormControl>
        </Box>

        <FormControl fullWidth margin='dense'>
          <PriceSummaryInput
            values={{
              currency: currency || undefined,
              subTotal: value?.price,
              discount: value?.discount,
              tax: value?.tax,
              insurance: value?.insurance,
              total: value?.total,
            }}
            formikProps={{
              errors: {
                // currency: typedErrors?.currency,
                subTotal: typedErrors?.price,
                discount: typedErrors?.discount,
                tax: typedErrors?.tax,
                insurance: typedErrors?.insurance,
                total: typedErrors?.total,
              },
            }}
            displayProps={{
              insurance: true,
              calcExplanationInline: false,
              calcExplanationModal: true,
            }}
            inputsProps={{
              all: {
                margin: "none",
                ...inputsProps?.all,
              },
              subTotal: {
                label: `Price per ${
                  value?.period ? enumService.getEnumValueName("BillingPeriod", value?.period) : "-"
                }`,
                required: true,
              },
              total: {
                required: true,
              },
              tax: {
                vehicleTaxCalcRequestParams: vehicleTaxCalcRequestParams,
              },
            }}
            onChange={(newValue) => {
              onChange({
                currency: newValue?.currency || currency || undefined,
                newValue: {
                  ...value,
                  price: newValue?.subTotal,
                  discount: newValue?.discount,
                  tax: newValue?.tax,
                  insurance: newValue?.insurance,
                  total: newValue?.total,
                },
              });
            }}
          />
        </FormControl>

        {generalError && <FormHelperText error>{generalError}</FormHelperText>}
      </Stack>
    );
  },
);
